/**
 * Storage service – upload video to /videos/{userId}/..., avatar to /avatars/{userId}/...
 * Delete video by URL. No UI; used by use cases only.
 */
import { ref, uploadBytesResumable, getDownloadURL, deleteObject } from 'firebase/storage';
import { storage } from './config';

/** Get storage path from Firebase download URL (e.g. .../o/videos%2Fuid%2Ffile?...) */
export function getStoragePathFromDownloadUrl(downloadUrl) {
  try {
    const match = downloadUrl.match(/\/o\/(.+?)(\?|$)/);
    if (!match) return null;
    return decodeURIComponent(match[1]);
  } catch {
    return null;
  }
}

export async function uploadLiftVideo(userId, file) {
  const baseName = (file.name || `recorded_${Date.now()}.webm`).replace(/\s/g, '_').replace(/[^a-zA-Z0-9._-]/g, '_');
  const filename = `${Date.now()}_${baseName}`;
  const path = `videos/${userId}/${filename}`;
  const storageRef = ref(storage, path);
  const snapshot = await uploadBytesResumable(storageRef, file);
  const url = await getDownloadURL(snapshot.ref);
  return url;
}

/** Upload with progress; calls onProgress(0-100). Returns download URL. */
export function uploadLiftVideoWithProgress(userId, file, onProgress) {
  const baseName = (file.name || `recorded_${Date.now()}.webm`).replace(/\s/g, '_').replace(/[^a-zA-Z0-9._-]/g, '_');
  const filename = `${Date.now()}_${baseName}`;
  const path = `videos/${userId}/${filename}`;
  const storageRef = ref(storage, path);
  return new Promise((resolve, reject) => {
    const task = uploadBytesResumable(storageRef, file);
    task.on(
      'state_changed',
      (snap) => {
        const pct = snap.totalBytes > 0 ? Math.round((100 * snap.bytesTransferred) / snap.totalBytes) : 0;
        onProgress?.(Math.min(100, pct));
      },
      reject,
      async () => {
        const url = await getDownloadURL(task.snapshot.ref);
        onProgress?.(100);
        resolve(url);
      }
    );
  });
}

/** Delete a file in Storage by its download URL (must be same bucket / videos/{userId}/...). */
export async function deleteLiftVideoByUrl(downloadUrl) {
  const path = getStoragePathFromDownloadUrl(downloadUrl);
  if (!path || !path.startsWith('videos/')) throw new Error('Invalid video URL');
  const storageRef = ref(storage, path);
  await deleteObject(storageRef);
}

/**
 * Delete any upload we produced (lift video, challenge video, avatar) given its
 * download URL. Silently ignores URLs outside known safe prefixes and any
 * "already deleted" errors — safe to call in an error-recovery `finally`/`catch`
 * path where we don't want cleanup to mask the original error.
 */
export async function safeDeleteUploadByUrl(downloadUrl) {
  try {
    const path = getStoragePathFromDownloadUrl(downloadUrl);
    if (!path) return false;
    const safe = ['videos/', 'avatars/', 'challenges/', 'coaches/'].some((p) => path.startsWith(p));
    if (!safe) return false;
    const storageRef = ref(storage, path);
    await deleteObject(storageRef);
    return true;
  } catch {
    return false;
  }
}

/** Upload challenge video; path: challenges/{challengeId}/videos/{userId}.ext. Returns download URL. */
export async function uploadChallengeVideo(challengeId, userId, file) {
  const ext = (file.name && file.name.split('.').pop()) || 'webm';
  const safeExt = ext.replace(/\s/g, '_').replace(/[^a-zA-Z0-9]/g, '');
  const path = `challenges/${challengeId}/videos/${userId}.${safeExt}`;
  const storageRef = ref(storage, path);
  const snapshot = await uploadBytesResumable(storageRef, file);
  return getDownloadURL(snapshot.ref);
}

/** Upload challenge video with progress; calls onProgress(0-100). Returns download URL. */
export function uploadChallengeVideoWithProgress(challengeId, userId, file, onProgress) {
  const ext = (file.name && file.name.split('.').pop()) || 'webm';
  const safeExt = ext.replace(/\s/g, '_').replace(/[^a-zA-Z0-9]/g, '');
  const path = `challenges/${challengeId}/videos/${userId}.${safeExt}`;
  const storageRef = ref(storage, path);
  return new Promise((resolve, reject) => {
    const task = uploadBytesResumable(storageRef, file);
    task.on(
      'state_changed',
      (snap) => {
        const pct = snap.totalBytes > 0 ? Math.round((100 * snap.bytesTransferred) / snap.totalBytes) : 0;
        onProgress?.(Math.min(100, pct));
      },
      reject,
      async () => {
        const url = await getDownloadURL(task.snapshot.ref);
        onProgress?.(100);
        resolve(url);
      }
    );
  });
}

/** Upload profile image; returns public URL. */
export async function uploadAvatar(userId, file) {
  const ext = (file.name && file.name.split('.').pop()) || 'jpg';
  const filename = `${Date.now()}.${ext.replace(/\s/g, '')}`;
  const path = `avatars/${userId}/${filename}`;
  const storageRef = ref(storage, path);
  const snapshot = await uploadBytesResumable(storageRef, file);
  return getDownloadURL(snapshot.ref);
}
